import { ShiftType } from "@m7-health/shared-utils";

import { IUnitBasic } from "~/routes/api/types";

import { ISchedule, IScheduleShiftType, IStaffDetails, IStaffShift, IUnit } from "@/api";
import { TSx } from "@/common/types";

/**
 * Types for the ONE shift editor component
 */

export type TShiftEditorProps = {
  staffShift: IStaffShift;
  updateStaffShift: (staffShift: IStaffShift) => void;
  deleteStaffShift: (staffShift: IStaffShift) => void;
  availableShiftTypes: IScheduleShiftType[];
  unitOfShift: IUnit | IUnitBasic;
  // readonly in general is used to prevent changing of anything
  // eg. used from scheduler grid when shift is away from unit it is being seen from
  readonly: boolean;
  // readonlyUnit is used to prevent changing the unit of the shift
  // eg. used from scheduler grid
  readonlyUnit: boolean;
  staffDetails: IStaffDetails;
  setShiftsValidity: React.Dispatch<
    React.SetStateAction<{
      [key: IStaffShift["id"]]: boolean;
    }>
  >;
  showHistory?: boolean;
  isLastEdited?: boolean;
};

/**
 * Types for the wrapper that handles all shifts for a staff on a given day
 */
type TPropsBase = {
  staffShifts: IStaffShift[];
  staffShiftsWithEdits?: IStaffShift[];
  readonly?: boolean;
  isLoading?: boolean;
  readonlyUnit?: boolean;
  staffDetails?: IStaffDetails;
  unitsByScheduleId: Record<ISchedule["id"], IUnit | IUnitBasic>;
  newStaffShiftScaffold?:
    | (Pick<IStaffShift, "scheduleId" | "date"> & {
        shiftTypeKey?: IStaffShift["shiftTypeKey"];
        scheduleType?: IStaffShift["scheduleType"];
      })
    | null;
  readonlyIfShiftNotOnSelectedUnit?: boolean;
  sx?: TSx;
};

type TPropsWithBuffer = TPropsBase & {
  autoSave?: boolean;
  saveChanges?: ({
    toCreate,
    toDelete,
    toUpdate,
  }: {
    toCreate: IStaffShift[];
    toDelete: IStaffShift[];
    toUpdate: IStaffShift[];
  }) => void;
  cancelChanges?: () => void;
  onChange?: (updatedShift: IStaffShift, originalShift: IStaffShift) => void;

  onCreate?: never;
  onUpdate?: never;
  onDelete?: never;
};

type TPropsWithInstantPropagation = TPropsBase & {
  autoSave?: never;
  saveChanges?: never;
  cancelChanges?: never;
  onChange?: never;

  onCreate?: (staffShift: IStaffShift) => void;
  onUpdate?: (updatedShift: IStaffShift, originalShift: IStaffShift) => void;
  onDelete?: (staffShift: IStaffShift) => void;
};

export type TShiftsEditorProps = TPropsWithBuffer | TPropsWithInstantPropagation;

export const EMPTY_SHIFT_TYPE_KEY = "__TO_BE_SELECTED__" as ShiftType.Key;
